import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AdminLayout from '../components/AdminLayout.jsx';
import { api, uploadUrl } from '../../api/client.js';
import { useSettings } from '../../context/SettingsContext.jsx';
import { Swal2 } from '../../lib/swal.js';
import { MORPH_BTN, MORPH_BTN_SM } from '../../lib/ui.js';
import { DAY_NAMES, formatOperatingHours } from '../../lib/hours.js';

// Sama persis dengan daftar ikon di admin/settings.php versi PHP
const ICONS = [
  'pets', 'cruelty_free', 'bug_report', 'eco', 'forest', 'nature', 'park', 'water_drop',
  'yard', 'emoji_nature', 'spa', 'local_florist', 'cloud', 'wb_sunny', 'nights_stay', 'storm',
  'star', 'favorite', 'thumb_up', 'celebration', 'cake', 'card_giftcard', 'emoji_events',
  'military_tech', 'workspace_premium', 'verified', 'shield', 'security', 'lock',
  'home', 'house', 'apartment', 'villa', 'cottage', 'store', 'storefront', 'business',
  'school', 'auto_stories', 'science', 'biotech', 'health_and_safety', 'medical_services',
  'camera', 'photo_camera', 'image', 'photo_library', 'collections', 'art_track',
  'music_note', 'headphones', 'piano', 'queue_music', 'album',
  'directions_run', 'sports', 'fitness_center', 'pool', 'kayaking', 'hiking',
  'restaurant', 'local_cafe', 'local_pizza', 'bakery_dining', 'wine_bar', 'kitchen',
  'flight', 'directions_car', 'directions_bike', 'train', 'sailing', 'rocket_launch',
  'phone', 'mail', 'chat', 'forum', 'campaign', 'notifications',
  'settings', 'tune', 'dashboard', 'analytics', 'bar_chart', 'pie_chart',
  'shopping_cart', 'local_mall', 'sell', 'payments', 'account_balance', 'savings',
  'person', 'group', 'groups', 'support_agent', 'manage_accounts', 'badge',
  'bolt', 'power', 'wb_incandescent', 'emoji_objects', 'lightbulb',
  'palette', 'brush', 'design_services', 'draw', 'format_paint',
  'map', 'explore', 'place', 'location_on', 'my_location', 'navigation',
  'calendar_month', 'event', 'schedule', 'alarm', 'timer', 'hourglass_top',
  'build', 'construction', 'handyman', 'plumbing', 'electrical_services',
  'local_hospital', 'healing', 'coronavirus', 'vaccines', 'stethoscope',
];

export default function SettingsPage() {
  const { settings: liveSettings, refresh } = useSettings();
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [activeSection, setActiveSection] = useState('identitas');

  const logoModeContainerRef = useRef(null);
  const logoModeBtnRefs = useRef({});
  const [logoModeIndicator, setLogoModeIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    api.get('/admin/settings').then(({ data }) => {
      setForm(data.settings);
      setPreview(data.settings.app_logo ? uploadUrl(data.settings.app_logo) : '');
    });
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const logoMode = form.app_logo_mode === 'logo' ? 'logo' : 'icon';
  const setMode = (mode) => setForm((f) => ({ ...f, app_logo_mode: mode }));

  useLayoutEffect(() => {
    const measure = () => {
      const btn = logoModeBtnRefs.current[logoMode];
      const container = logoModeContainerRef.current;
      if (!btn || !container) return;
      const cRect = container.getBoundingClientRect();
      const bRect = btn.getBoundingClientRect();
      setLogoModeIndicator({ left: bRect.left - cRect.left, width: bRect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [logoMode, activeSection]);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setRemoveLogo(false);
    setPreview(URL.createObjectURL(f));
  };

  const onRemoveLogo = () => {
    setFile(null);
    setPreview('');
    setRemoveLogo(true);
    setMode('icon');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (file) fd.append('logo', file);
      if (removeLogo) fd.append('remove_logo', '1');
      const { data } = await api.put('/admin/settings', fd);
      await refresh();
      setRemoveLogo(false);
      setFile(null);
      Swal2.toast('success', data.message);
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal menyimpan.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border text-sm outline-none bg-neutral-50 dark:bg-neutral-800 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20';
  const labelCls = 'block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1.5';
  const subLabelCls = 'text-[11px] text-neutral-400 mb-1 block';

  if (!Object.keys(form).length) return <AdminLayout title="Pengaturan Situs"><p className="text-neutral-400">Memuat...</p></AdminLayout>;

  const previewHours = formatOperatingHours(form);

  const SECTIONS = [
    { id: 'identitas', icon: 'badge', title: 'Identitas Situs', desc: 'Nama, tagline, dan warna utama yang tampil di seluruh halaman.' },
    { id: 'icon-pill', icon: 'award_star', title: 'Ikon Title Pill', desc: 'Ikon Google atau logo sendiri yang tampil di pill navbar sebelah nama situs.' },
    { id: 'kontak', icon: 'call', title: 'Kontak', desc: 'Nomor dan alamat yang tampil di halaman Hubungi Kami.' },
    { id: 'sosial', icon: 'share', title: 'Media Sosial', desc: 'Kosongkan sebuah kolom untuk menyembunyikan ikonnya dari situs.' },
    { id: 'jam', icon: 'schedule', title: 'Jam Operasional', desc: 'Status buka/tutup di situs mengikuti pengaturan ini secara otomatis.' },
    { id: 'maps', icon: 'location_on', title: 'Google Maps', desc: 'Ditampilkan di section Hubungi Kami.' },
  ];

  const Section = ({ icon, title, desc, children }) => (
    <section>
      <div className="flex items-start gap-2.5 mb-4">
        <span className="material-symbols-outlined text-forest-500 text-xl mt-0.5">{icon}</span>
        <div>
          <h3 className="text-sm font-extrabold text-neutral-800 dark:text-white leading-tight">{title}</h3>
          {desc && <p className="text-xs text-neutral-400 mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="space-y-4 pl-[30px]">{children}</div>
    </section>
  );

  // Isi field per section, dipakai ulang baik untuk panel kanan (desktop) maupun list stacked (mobile).
  const renderContent = (id) => {
    switch (id) {
      case 'identitas':
        return (
          <>
            <div>
              <label className={labelCls}>Nama Situs</label>
              <input className={inputCls} value={form.site_name || ''} onChange={set('site_name')} />
            </div>
            <div>
              <label className={labelCls}>Tagline</label>
              <textarea rows={2} className={`${inputCls} resize-none`} value={form.tagline || ''} onChange={set('tagline')} />
            </div>
            <div>
              <label className={labelCls}>Warna Utama</label>
              <input type="color" className="w-full h-11 rounded-xl border border-neutral-200 dark:border-neutral-700" value={form.app_color || '#4A7A3A'} onChange={set('app_color')} />
            </div>
          </>
        );
      case 'icon-pill':
        return (
          <>
            {/* Saklar mode: Ikon Google vs Logo Sendiri — pola sliding-pill sama dengan toggle Grid/Tabel */}
            <div ref={logoModeContainerRef} className="relative inline-flex items-center gap-1 p-1 rounded-full border bg-white dark:bg-neutral-900" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
              <div
                className="absolute top-1 bottom-1 left-0 rounded-full transition-transform duration-300 will-change-transform"
                style={{ background: 'var(--accent)', width: logoModeIndicator.width, transform: `translateX(${logoModeIndicator.left}px)`, transitionTimingFunction: 'var(--ease-spring)' }}
              />
              <button
                type="button"
                ref={(el) => { logoModeBtnRefs.current.icon = el; }}
                onClick={() => setMode('icon')}
                aria-pressed={logoMode === 'icon'}
                className={`relative z-10 flex items-center gap-1.5 px-3.5 h-8 rounded-full text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${MORPH_BTN_SM}`}
                style={{ color: logoMode === 'icon' ? '#fff' : 'var(--accent)', transitionProperty: 'color, border-radius, transform' }}
              >
                <span className="material-symbols-outlined text-sm">emoji_symbols</span> Ikon Google
              </button>
              <button
                type="button"
                ref={(el) => { logoModeBtnRefs.current.logo = el; }}
                onClick={() => setMode('logo')}
                aria-pressed={logoMode === 'logo'}
                className={`relative z-10 flex items-center gap-1.5 px-3.5 h-8 rounded-full text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${MORPH_BTN_SM}`}
                style={{ color: logoMode === 'logo' ? '#fff' : 'var(--accent)', transitionProperty: 'color, border-radius, transform' }}
              >
                <span className="material-symbols-outlined text-sm">upload</span> Logo Sendiri
              </button>
            </div>

            {logoMode === 'icon' ? (
              <div>
                <div className="relative mb-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-neutral-400 text-base">search</span>
                  <input
                    type="text" value={iconSearch} onChange={(e) => setIconSearch(e.target.value)}
                    placeholder="Cari ikon…"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm outline-none bg-neutral-50 dark:bg-neutral-900/40 text-neutral-900 dark:text-white border-neutral-200 dark:border-neutral-700 focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500 transition-all"
                  />
                </div>
                <div className="icon-grid rounded-xl border border-neutral-100 dark:border-neutral-700/50 p-1.5">
                  {ICONS.filter((ic) => ic.includes(iconSearch.toLowerCase())).map((ic) => (
                    <button type="button" key={ic} title={ic} onClick={() => setForm((f) => ({ ...f, app_icon: ic }))}
                      className={`rounded-lg flex items-center justify-center border transition-all duration-300 hover:rounded-2xl active:rounded-lg active:scale-90 ${form.app_icon === ic ? 'border-forest-500 bg-forest-500/10 text-forest-500' : 'border-transparent text-neutral-400 hover:border-forest-500/50 hover:text-forest-500 hover:bg-forest-500/5'}`}>
                      <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>{ic}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-neutral-400 mt-2">
                  Ikon terpilih: <span className="font-mono font-semibold text-neutral-600 dark:text-neutral-300">{form.app_icon || 'pets'}</span>
                </p>
              </div>
            ) : (
              <div>
                <p className="text-[11px] text-neutral-400 mb-2">
                  Logo akan menggantikan seluruh isi Title Pill (ikon &amp; nama situs tidak lagi ditampilkan). Gunakan PNG transparan.
                </p>
                <div className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/40 mb-2">
                  <div className="w-14 h-14 rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 flex items-center justify-center flex-shrink-0">
                    {preview
                      ? <img src={preview} className="w-full h-full object-contain" />
                      : <span className="material-symbols-outlined text-neutral-300 dark:text-neutral-600 text-2xl">image</span>}
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFile} className="text-xs text-neutral-500 flex-1" />
                </div>
                {preview && (
                  <button type="button" onClick={onRemoveLogo} className="text-[11px] font-bold text-red-500 hover:text-red-600 flex items-center gap-1 transition-all duration-300 active:scale-95">
                    <span className="material-symbols-outlined text-sm">delete</span> Hapus Logo
                  </button>
                )}
              </div>
            )}
          </>
        );
      case 'kontak':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Telepon / Hotline</label>
                <input className={inputCls} value={form.telepon || ''} onChange={set('telepon')} />
              </div>
              <div>
                <label className={labelCls}>WhatsApp (angka saja, cth 6281933667)</label>
                <input className={inputCls} value={form.whatsapp || ''} onChange={set('whatsapp')} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input className={inputCls} value={form.email || ''} onChange={set('email')} />
            </div>
            <div>
              <label className={labelCls}>Alamat</label>
              <textarea rows={2} className={`${inputCls} resize-none`} value={form.alamat || ''} onChange={set('alamat')} />
            </div>
          </>
        );
      case 'sosial':
        return (
          <>
            <div>
              <label className={labelCls}>Instagram (URL)</label>
              <input className={inputCls} value={form.instagram || ''} onChange={set('instagram')} />
            </div>
            <div>
              <label className={labelCls}>Facebook (URL)</label>
              <input className={inputCls} value={form.facebook || ''} onChange={set('facebook')} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label className={labelCls}>YouTube (URL)</label>
              <input className={inputCls} value={form.youtube || ''} onChange={set('youtube')} placeholder="https://youtube.com/@..." />
            </div>
            <div>
              <label className={labelCls}>TikTok (URL)</label>
              <input className={inputCls} value={form.tiktok || ''} onChange={set('tiktok')} placeholder="https://tiktok.com/@..." />
            </div>
          </>
        );
      case 'jam':
        return (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={subLabelCls}>Dari Hari</label>
                <select className={inputCls} value={form.hari_mulai ?? '1'} onChange={set('hari_mulai')}>
                  {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={subLabelCls}>Sampai Hari</label>
                <select className={inputCls} value={form.hari_selesai ?? '5'} onChange={set('hari_selesai')}>
                  {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={subLabelCls}>Jam Buka</label>
                <input type="time" className={inputCls} value={form.jam_buka || '08:00'} onChange={set('jam_buka')} />
              </div>
              <div>
                <label className={subLabelCls}>Jam Tutup</label>
                <input type="time" className={inputCls} value={form.jam_tutup || '17:00'} onChange={set('jam_tutup')} />
              </div>
            </div>
            <p className="text-xs text-neutral-400 bg-neutral-50 dark:bg-neutral-800/40 rounded-lg px-3 py-2">
              Pratinjau: <strong className="text-neutral-600 dark:text-neutral-300">{previewHours}</strong>
            </p>
          </>
        );
      case 'maps':
        return (
          <>
            <div>
              <label className={labelCls}>Nama Lokasi (kosongkan untuk sembunyikan header di atas peta)</label>
              <input className={inputCls} value={form.maps_label || ''} onChange={set('maps_label')} placeholder="Nama toko/kantor" />
            </div>
            <div>
              <label className={labelCls}>Link "Buka di Maps" (opsional)</label>
              <input className={inputCls} value={form.maps_view_url || ''} onChange={set('maps_view_url')} placeholder="https://www.google.com/maps/place/..." />
            </div>
            <div>
              <label className={labelCls}>Embed URL Google Maps (dari tombol "Share" → "Embed a map")</label>
              <textarea rows={2} className={`${inputCls} resize-none text-xs`} value={form.maps_embed_url || ''} onChange={set('maps_embed_url')} placeholder="https://www.google.com/maps/embed?pb=..." />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const active = SECTIONS.find((s) => s.id === activeSection) || SECTIONS[0];
  const saveBtn = (
    <button disabled={saving} type="submit" className={`w-full lg:w-auto px-6 py-3 rounded-xl font-bold text-white text-sm ${MORPH_BTN}`} style={{ background: liveSettings.app_color || 'var(--accent)' }}>
      {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
    </button>
  );

  return (
    <AdminLayout title="Pengaturan Situs">
      <form onSubmit={submit} className="lg:flex lg:items-start lg:gap-6">

        {/* Menu section — desktop: dipilih dulu di kiri, mobile: disembunyikan (di bawah tampil stacked) */}
        <div className="hidden lg:block w-64 flex-shrink-0 sticky top-24 self-start">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-black/5 dark:border-white/5 p-2 space-y-0.5">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSection(s.id)}
                className={`dn-link w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-left ${MORPH_BTN_SM} ${
                  activeSection === s.id ? 'dn-active text-white' : 'text-[#262626] dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
                style={activeSection === s.id ? { background: liveSettings.app_color || 'var(--accent)' } : undefined}
              >
                <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: `'FILL' ${activeSection === s.id ? 1 : 0}` }}>{s.icon}</span>
                {s.title}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {/* Desktop: hanya section yang dipilih, muncul di kanan (dual page) */}
          <div className="hidden lg:block bg-white dark:bg-neutral-900 rounded-2xl border border-black/5 dark:border-white/5 p-6">
            <Section icon={active.icon} title={active.title} desc={active.desc}>
              {renderContent(active.id)}
            </Section>
            <div className="pt-6 mt-6 border-t border-neutral-100 dark:border-neutral-800">{saveBtn}</div>
          </div>

          {/* Mobile & tablet: semua section ditumpuk vertikal seperti biasa */}
          <div className="lg:hidden bg-white dark:bg-neutral-900 rounded-2xl border border-black/5 dark:border-white/5 p-4 sm:p-6 space-y-8">
            {SECTIONS.map((s, i) => (
              <div key={s.id}>
                <Section icon={s.icon} title={s.title} desc={s.desc}>
                  {renderContent(s.id)}
                </Section>
                {i < SECTIONS.length - 1 && <div className="border-t border-neutral-100 dark:border-neutral-800 mt-8" />}
              </div>
            ))}
            <div className="pt-2">{saveBtn}</div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
